import { useRef, useEffect, useState } from 'preact/hooks';
import { marked, type Tokens } from 'marked';
import DOMPurify from 'dompurify';
import type { WidgetConfig, MessageEntry } from '../lib/types';

interface MessageListProps {
  config: WidgetConfig;
  messages: MessageEntry[];
  isSending: boolean;
  streamContent: string;
  error: string | null;
  onSend: (text: string) => void;
  onRetry: () => void;
}

function escapeAttr(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const SAFE_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function isSafeUrl(url: string): boolean {
  try {
    return SAFE_PROTOCOLS.has(new URL(url).protocol);
  } catch {
    const trimmed = url.trim().toLowerCase();
    return !trimmed.startsWith('javascript:') && !trimmed.startsWith('data:');
  }
}

const markedRenderer = new marked.Renderer();
markedRenderer.link = ({ href, title, text }: Tokens.Link) => {
  if (!isSafeUrl(href)) return `${text}`;
  const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
  return `<a href="${escapeAttr(href)}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
};

marked.setOptions({ breaks: true, gfm: true });
marked.use({ renderer: markedRenderer });

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function parseMarkdownToHtml(content: string): string {
  try {
    return marked.parse(content) as string;
  } catch {
    return escapeHtml(content);
  }
}

/**
 * Renders markdown content safely.
 * DOMPurify.sanitize is called inline in the JSX assignment so there is no
 * intermediate variable between the sanitizer output and dangerouslySetInnerHTML.
 * All assistant message rendering goes through this single component.
 */
function AssistantBubble({ content }: { content: string }): preact.JSX.Element {
  return (
    <div
      class="yd-msg yd-msg-assistant"
      // DOMPurify.sanitize called directly here — no intermediate variable.
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseMarkdownToHtml(content)) }}
    />
  );
}

let hasShownWelcome = false;

// Inline data-URI so the default needs no external host (was yobu's R2 bucket).
// Neutral chat-bubble glyph on a soft gray circle; merchants override it via
// message_avatar_url in chatbot settings.
const DEFAULT_AVATAR =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">' +
      '<circle cx="16" cy="16" r="16" fill="#e5e7eb"/>' +
      '<path d="M9 11.5A2.5 2.5 0 0 1 11.5 9h9A2.5 2.5 0 0 1 23 11.5v6a2.5 2.5 0 0 1-2.5 2.5H14l-3.6 3v-3h.1A2.5 2.5 0 0 1 9 17.5z" fill="#6b7280"/>' +
      '<circle cx="13.2" cy="14.5" r="1.2" fill="#e5e7eb"/>' +
      '<circle cx="16.5" cy="14.5" r="1.2" fill="#e5e7eb"/>' +
      '<circle cx="19.8" cy="14.5" r="1.2" fill="#e5e7eb"/>' +
    '</svg>'
  );

export function MessageList({
  config,
  messages,
  isSending,
  streamContent,
  error,
  onSend,
  onRetry,
}: MessageListProps): preact.JSX.Element {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [welcomePhase, setWelcomePhase] = useState<'typing' | 'message'>(
    hasShownWelcome ? 'message' : 'typing',
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, streamContent]);

  useEffect(() => {
    if (welcomePhase !== 'typing') return;
    const timer = setTimeout(() => {
      hasShownWelcome = true;
      setWelcomePhase('message');
    }, 1500);
    return () => clearTimeout(timer);
  }, [welcomePhase]);

  const showSuggestions = messages.length === 0 && !isSending;
  const avatarSrc = config.message_avatar_url || DEFAULT_AVATAR;

  function AvatarRow({ children }: { children: preact.ComponentChildren }): preact.JSX.Element {
    return (
      <div class="yd-msg-row">
        <img class="yd-msg-avatar" src={avatarSrc} alt="" />
        {children}
      </div>
    );
  }

  return (
    <div class="yd-messages">
      {welcomePhase === 'typing' && messages.length === 0 && !isSending && (
        <AvatarRow>
          <div class="yd-typing">
            <span class="yd-dots">
              <span class="yd-dot" />
              <span class="yd-dot" />
              <span class="yd-dot" />
            </span>
          </div>
        </AvatarRow>
      )}

      {welcomePhase === 'message' && (
        <>
          <AvatarRow>
            <AssistantBubble content={config.welcome_message || 'Hi! How can I help you today?'} />
          </AvatarRow>
          {showSuggestions && config.suggested_questions?.length > 0 && (
            <div class="yd-suggestions">
              {config.suggested_questions.map((q) => (
                <button class="yd-suggestion" key={q} onClick={() => onSend(q)}>
                  {q}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {messages.map((entry, i) => {
        const msg = entry.data;
        return (
          <div key={`${msg.ts}-${i}`}>
            {msg.role === 'assistant' ? (
              <AvatarRow>
                <AssistantBubble content={msg.content} />
              </AvatarRow>
            ) : (
              <div class="yd-msg yd-msg-user">
                {msg.content}
              </div>
            )}
          </div>
        );
      })}

      {/* Streaming content */}
      {isSending && streamContent && (
        <AvatarRow>
          <AssistantBubble content={streamContent} />
        </AvatarRow>
      )}

      {/* Typing indicator */}
      {isSending && !streamContent && (
        <AvatarRow>
          <div class="yd-typing">
            <span class="yd-dots">
              <span class="yd-dot" />
              <span class="yd-dot" />
              <span class="yd-dot" />
            </span>
          </div>
        </AvatarRow>
      )}

      {/* Error state */}
      {error && (
        <div class="yd-error">
          <div>{error}</div>
          <button class="yd-retry-btn" onClick={onRetry}>Try again</button>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
}
